'use client';

import { BREAKPOINTS, DEVICE_TYPE, clsx, useDeviceType, usePageLayout, useWindowSize } from '@libs/common-next';
import { LinkList, LinkListItem } from '@petsmart-ui/sparky';

import { dataLayerPush, getBaseUrlJoined, layoutEvents, trackMainNavLinkClick } from '@libs/analytics';
import type { LinkAction, Link } from '../../types';
import { propsMapper } from '../../utils/propsMapper';
import './SecondaryNavigation.scss';
import type { SecondaryNavigationProps } from './types';
import { getLinkFromLinkOrLinkAction } from '../../utils/getLinkFromLinkOrLinkAction';

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

export default function SecondaryNavigation({
    navigationLinks,
    tabletNavigationLinks,
    isHeader = true,
    className,
}: SecondaryNavigationProps) {
    const size = useWindowSize();
    const { flags } = usePageLayout();

    const classes = clsx(isHeader ? 'link-list' : 'link-list link-list__drawer', className);
    const linkBreakpointClass = size > BREAKPOINTS.lg ? 'sparky-u-margin-left-md' : 'sparky-u-margin-left-sm';
    const linkClasses = clsx(
        isHeader ? `link-list--normal ${linkBreakpointClass}` : 'link-list--big sparky-u-margin-left-md'
    );
    const deviceSize = useDeviceType();
    const links = propsMapper<LinkAction | Link>(
        (deviceSize === DEVICE_TYPE.DESKTOP ? navigationLinks : tabletNavigationLinks) ?? []
    );

    const flatLinks = links.map(linkOrLinkAction => getLinkFromLinkOrLinkAction(linkOrLinkAction));

    const signUpText = 'Sign up or create account';
    return (
        <LinkList
            data-testid="secondary-navigation"
            className={classes}
            size={isHeader ? 'sm' : undefined}
            behavior={isHeader ? 'horizontal' : undefined}
        >
            {flatLinks.map(({ actionText, webUrl, iconName }) => (
                <LinkListItem
                    //  @ts-expect-error expects social icons and regular icons, while receives only regular icons
                    iconName={iconName}
                    iconPosition="after"
                    className={linkClasses}
                    onClick={() => {
                        trackMainNavLinkClick(actionText);

                        dataLayerPush(
                            trackingData({
                                selection: actionText,
                                link_url: getBaseUrlJoined(webUrl),
                            })
                        );
                    }}
                    key={webUrl}
                    href={webUrl}
                    text={actionText}
                />
            ))}
            {deviceSize !== DEVICE_TYPE.DESKTOP && !flags.legacyDisplay && (
                <LinkListItem
                    iconPosition="after"
                    className="link-list--sign-in sparky-u-margin-left-md"
                    onClick={() => {
                        trackMainNavLinkClick(signUpText);
                    }}
                    key="sign-in"
                    href="#"
                    text={signUpText}
                />
            )}
        </LinkList>
    );
}
